import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Select, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { dateTime, money } from '@/lib/format';
import { parseCsv } from '@/lib/csv';
import {
  addDoNotContact, deleteProspectCategory, deleteProspectProvider, getGoogleCategories, getProspectSetup, importProspectSheet, removeDoNotContact,
  saveExcludedDomains, getPipelines, saveDiscoverySettings, saveProspectCadence, saveProspectCategory, saveProspectProvider,
} from '@/data/client';
import type { ProspectCategory, ProspectProvider, ProspectSetup } from '@/data/types';
import { useMembers } from '@/data/hooks';
import { FormError } from '@/components/crm/Modal';

/* Managers only (the API enforces it). Everything the sheet kept on its side tabs. */
export function ProspectSetupPanel() {
  const { data } = useQuery({ queryKey: ['prospectSetup'], queryFn: getProspectSetup });
  // Google's real category list, to flag ids a search would get nothing for.
  const { data: google } = useQuery({ queryKey: ['googleCategories'], queryFn: getGoogleCategories, enabled: !!data?.configured, staleTime: 86_400_000, retry: false });
  const known = google ? new Set(google.map((g) => g.code)) : null;
  if (!data) return null;
  return (
    <div className="crm-list">
      <DiscoveryCard discovery={data.discovery} />
      <CadenceCard cadence={data.cadence} budget={data.monthlyBudget} spent={data.spentThisMonth} />
      <Card padding={false} className="crm-section">
        <div className="crm-section__head"><h3>Categories and keywords</h3></div>
        <p className="crm-cell-sub">Google Business category ids are what Maps is searched by (e.g. <code>roofing_contractor</code>). Keywords use <code>{'{city}'}</code> and <code>{'{state}'}</code>; up to 20 active per category. Start a line with # to pause it.</p>
        <div className="crm-list">
          {data.categories.map((c) => <CategoryEditor key={c.id} category={c} known={known} />)}
          <CategoryEditor known={known} />
        </div>
        {!data.configured && <p className="crm-cell-sub">Once DataForSEO is connected, each category's ids are checked against Google's list here.</p>}
      </Card>
      {google && <GoogleCategoryLookup list={google} />}
      <ProvidersCard providers={data.providers} />
      <ExcludedCard domains={data.excludedDomains} />
      <DncCard rows={data.doNotContact} />
      <SheetImport />
    </div>
  );
}

const useRefresh = () => {
  const qc = useQueryClient();
  return () => { for (const k of ['prospectSetup', 'prospects']) void qc.invalidateQueries({ queryKey: [k] }); };
};

/* Who owns new prospects (George), who hosts the discovery call (Matt) and where
   a booked prospect's deal starts (Discovery Discussion Scheduled). */
function DiscoveryCard({ discovery }: { discovery: ProspectSetup['discovery'] }) {
  const refresh = useRefresh();
  const { ownerOptions } = useMembers();
  const { data: pipelines = [] } = useQuery({ queryKey: ['pipelines'], queryFn: getPipelines, staleTime: 10 * 60_000 });
  const [f, setF] = useState({ hostUserId: discovery.hostUserId ?? '', stageId: discovery.stageId ?? '', defaultOwnerUserId: discovery.defaultOwnerUserId ?? '' });
  const save = useMutation({
    mutationFn: () => saveDiscoverySettings({ hostUserId: f.hostUserId || null, stageId: f.stageId || null, defaultOwnerUserId: f.defaultOwnerUserId || null }),
    onSuccess: refresh,
  });
  const stages = pipelines.flatMap((pl) => pl.stages.filter((s) => s.kind === 'open' && !s.nurture)
    .map((s) => ({ value: s.id, label: pipelines.length > 1 ? `${pl.name} · ${s.name}` : s.name })));
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Discovery calls</h3></div>
      <div className="crm-form">
        <div className="crm-grid2">
          <Select label="New prospects go to" placeholder="Unassigned" options={ownerOptions} value={f.defaultOwnerUserId} onChange={(e) => setF({ ...f, defaultOwnerUserId: e.target.value })}
            hint="Searches and imports assign new prospects to this person" />
          <Select label="Discovery calls are with" placeholder="Choose…" options={ownerOptions} value={f.hostUserId} onChange={(e) => setF({ ...f, hostUserId: e.target.value })}
            hint="The default host; the rep can pick someone else per call" />
          <Select label="Booked prospects start in" placeholder="First pipeline stage" options={stages} value={f.stageId} onChange={(e) => setF({ ...f, stageId: e.target.value })}
            hint="The deal's stage when a discovery call is booked" />
        </div>
        <div><Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button></div>
        <FormError error={save.error} />
      </div>
    </Card>
  );
}

function CadenceCard({ cadence, budget, spent }: { cadence: { attempts: number; windowDays: number }; budget: number; spent: number }) {
  const refresh = useRefresh();
  const [f, setF] = useState({ attempts: String(cadence.attempts), windowDays: String(cadence.windowDays), monthlyBudget: String(budget) });
  const save = useMutation({ mutationFn: () => saveProspectCadence({ attempts: Number(f.attempts), windowDays: Number(f.windowDays), monthlyBudget: Number(f.monthlyBudget) }), onSuccess: refresh });
  const gap = Number(f.attempts) > 1 ? (Number(f.windowDays) / (Number(f.attempts) - 1)) : Number(f.windowDays);
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Call rotation and budget</h3><span className="crm-cell-sub">{money(spent)} spent this month</span></div>
      <div className="crm-form">
        <div className="crm-grid2">
          <Input label="Attempts before a prospect goes cold" type="number" min={1} max={20} value={f.attempts} onChange={(e) => setF({ ...f, attempts: e.target.value })} />
          <Input label="Over how many days" type="number" min={1} max={90} value={f.windowDays} onChange={(e) => setF({ ...f, windowDays: e.target.value })}
            hint={Number.isFinite(gap) && gap > 0 ? `A call every ${gap % 1 ? gap.toFixed(1) : gap} days until answered` : undefined} />
          <Input label="Monthly search budget ($)" type="number" min={0} step="5" value={f.monthlyBudget} onChange={(e) => setF({ ...f, monthlyBudget: e.target.value })}
            hint="DataForSEO spend; searches stop when it's reached" />
        </div>
        <p className="crm-cell-sub">A call back resets the count. Not interested, wrong number and do not contact close the prospect.</p>
        <div><Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button></div>
        <FormError error={save.error} />
      </div>
    </Card>
  );
}

const toLines = (c?: ProspectCategory) => (c?.keywords ?? []).map((k) => (k.active ? k.keyword : `# ${k.keyword}`)).join('\n');

const unknownIds = (ids: string, known: Set<string> | null) =>
  known ? ids.split(',').map((x) => x.trim()).filter((x) => x && !known.has(x)) : [];

function CategoryEditor({ category, known }: { category?: ProspectCategory; known: Set<string> | null }) {
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: category?.name ?? '', googleIds: category?.googleIds ?? '', keywords: toLines(category) });
  useEffect(() => { setF({ name: category?.name ?? '', googleIds: category?.googleIds ?? '', keywords: toLines(category) }); }, [category]);
  const save = useMutation({
    mutationFn: () => saveProspectCategory(category?.id ?? null, {
      name: f.name, googleIds: f.googleIds,
      keywords: f.keywords.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => (l.startsWith('#') ? { keyword: l.replace(/^#\s*/, ''), active: false } : { keyword: l, active: true })),
    }),
    onSuccess: () => { refresh(); setOpen(false); if (!category) setF({ name: '', googleIds: '', keywords: '' }); },
  });
  const remove = useMutation({ mutationFn: () => deleteProspectCategory(category!.id), onSuccess: refresh });
  if (!open) {
    return category
      ? (
        <button type="button" className="pq-cat" onClick={() => setOpen(true)}>
          <strong>{category.name}</strong>
          <span className="crm-cell-sub">
            {category.googleIds} · {category.keywords.filter((k) => k.active).length} keywords
            {unknownIds(category.googleIds, known).length > 0 && <span className="pq-warn"> · not a Google category: {unknownIds(category.googleIds, known).join(', ')}</span>}
          </span>
          <Icon name="edit" size={14} />
        </button>
      )
      : <div><Button size="sm" variant="outline" iconLeft={<Icon name="plus" size={14} />} onClick={() => setOpen(true)}>Add a category</Button></div>;
  }
  return (
    <div className="crm-form pq-cat pq-cat--open">
      <div className="crm-grid2">
        <Input label="Category" placeholder="Plumbers" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input label="Google category ids" placeholder="plumber, drainage_service" value={f.googleIds} onChange={(e) => setF({ ...f, googleIds: e.target.value })}
          hint={unknownIds(f.googleIds, known).length ? `Not a Google category: ${unknownIds(f.googleIds, known).join(', ')} — use the lookup below` : undefined} />
      </div>
      <Textarea label="Keywords (one per line)" rows={8} value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} placeholder={'plumber {city}\nemergency plumber {city}'} />
      <div className="crm-record__actions">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
        {category && <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Delete ${category.name}? Prospects already found keep their category.`)) remove.mutate(); }}>Delete</Button>}
      </div>
      <FormError error={save.error ?? remove.error} />
    </div>
  );
}

/* The sheet's "Look up Google category IDs", as a search box. */
function GoogleCategoryLookup({ list }: { list: { code: string; name: string }[] }) {
  const [q, setQ] = useState('');
  const term = q.trim().toLowerCase();
  const hits = term.length < 2 ? [] : list.filter((g) => g.name.toLowerCase().includes(term) || g.code.includes(term)).slice(0, 30);
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Look up Google category ids</h3><span className="crm-cell-sub">{list.length.toLocaleString()} categories</span></div>
      <Input aria-label="Search Google categories" placeholder="e.g. gutter, garage door, security" value={q} onChange={(e) => setQ(e.target.value)} />
      {hits.length > 0 && (
        <ul className="crm-people">
          {hits.map((g) => <li key={g.code}><div><strong>{g.name}</strong><span className="crm-cell-sub"><code>{g.code}</code></span></div></li>)}
        </ul>
      )}
      {term.length >= 2 && !hits.length && <p className="crm-cell-sub">No Google category matches "{q}".</p>}
    </Card>
  );
}

/* Website providers for Find by search term: the text each one leaves in its
   clients' footers. Every business's homepage is read for these; a search can
   be limited to one provider's sites. */
function ProvidersCard({ providers }: { providers: ProspectProvider[] }) {
  const refresh = useRefresh();
  const [editing, setEditing] = useState<string | null>(null);
  const [f, setF] = useState({ name: '', signature: '' });
  const reset = () => { setEditing(null); setF({ name: '', signature: '' }); };
  const save = useMutation({ mutationFn: () => saveProspectProvider(editing, f), onSuccess: () => { reset(); refresh(); } });
  const remove = useMutation({ mutationFn: (id: string) => deleteProspectProvider(id), onSuccess: refresh });
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Website providers ({providers.length})</h3></div>
      <p className="crm-cell-sub">For Find by search term. The text a provider leaves on its clients' sites, e.g. "Marketing by RYNO Strategic Solutions" — matched anywhere on the homepage, ignoring case.</p>
      <div className="crm-form">
        <div className="pq-provider-add">
          <Input aria-label="Provider" placeholder="RYNO Strategic Solutions" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Input aria-label="Text on the site" placeholder="Marketing by RYNO Strategic Solutions" value={f.signature} onChange={(e) => setF({ ...f, signature: e.target.value })} />
          <Button size="sm" onClick={() => save.mutate()} disabled={!f.name.trim() || !f.signature.trim() || save.isPending}>{editing ? 'Save' : 'Add'}</Button>
          {editing && <Button size="sm" variant="ghost" onClick={reset}>Cancel</Button>}
        </div>
        <FormError error={save.error} />
      </div>
      {providers.length > 0 && (
        <ul className="crm-people">
          {providers.map((p) => (
            <li key={p.id}>
              <div><strong>{p.name}</strong><span className="crm-cell-sub">“{p.signature}”</span></div>
              <button type="button" className="lead-modal__x" aria-label={`Edit ${p.name}`} onClick={() => { setEditing(p.id); setF({ name: p.name, signature: p.signature }); }}><Icon name="edit" size={14} /></button>
              <button type="button" className="lead-modal__x" aria-label={`Remove ${p.name}`} onClick={() => { if (confirm(`Remove ${p.name}? Prospects already found keep their provider.`)) remove.mutate(p.id); }}><Icon name="x" size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ExcludedCard({ domains }: { domains: string[] }) {
  const refresh = useRefresh();
  const [text, setText] = useState(domains.join('\n'));
  useEffect(() => setText(domains.join('\n')), [domains]);
  const save = useMutation({ mutationFn: () => saveExcludedDomains(text.split(/[\n,]/).map((d) => d.trim()).filter(Boolean)), onSuccess: refresh });
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Excluded domains ({domains.length})</h3></div>
      <p className="crm-cell-sub">Directories, free site builders and national brands — never prospects. One per line.</p>
      <div className="crm-form">
        <Textarea aria-label="Excluded domains" rows={6} value={text} onChange={(e) => setText(e.target.value)} />
        <div><Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button></div>
        <FormError error={save.error} />
      </div>
    </Card>
  );
}

function DncCard({ rows }: { rows: { domain: string; company: string | null; reason: string | null; addedBy: string | null; createdAt: string }[] }) {
  const refresh = useRefresh();
  const [f, setF] = useState({ domain: '', company: '', reason: '' });
  const add = useMutation({ mutationFn: () => addDoNotContact(f), onSuccess: () => { setF({ domain: '', company: '', reason: '' }); refresh(); } });
  const remove = useMutation({ mutationFn: (domain: string) => removeDoNotContact(domain), onSuccess: refresh });
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Do not contact ({rows.length})</h3></div>
      <p className="crm-cell-sub">Never added by a search. Closing a prospect as "Do not contact" adds it here.</p>
      <div className="crm-form">
        <div className="pq-dnc-add">
          <Input aria-label="Domain" placeholder="acmeroofing.com" value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} />
          <Input aria-label="Company" placeholder="Company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} />
          <Input aria-label="Reason" placeholder="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
          <Button size="sm" onClick={() => add.mutate()} disabled={!f.domain || add.isPending}>Add</Button>
        </div>
        <FormError error={add.error} />
      </div>
      {rows.length > 0 && (
        <ul className="crm-people">
          {rows.map((r) => (
            <li key={r.domain}>
              <div><strong>{r.domain}</strong><span className="crm-cell-sub">{[r.company, r.reason, r.addedBy, dateTime(r.createdAt)].filter(Boolean).join(' · ')}</span></div>
              <button type="button" className="lead-modal__x" aria-label={`Remove ${r.domain}`} onClick={() => remove.mutate(r.domain)}><Icon name="x" size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* One-time move from the Google Sheet: File → Download → CSV on its Prospects
   and Do Not Contact tabs. Re-importing skips domains already here. */
function SheetImport() {
  const refresh = useRefresh();
  const [files, setFiles] = useState<{ prospects?: File; dnc?: File }>({});
  const run = useMutation({
    mutationFn: async () => {
      const rowsOf = async (file?: File) => {
        if (!file) return [];
        const { headers, rows } = parseCsv(await file.text());
        return rows.map((cells) => Object.fromEntries(headers.map((h, i) => [h.trim(), cells[i] ?? ''])));
      };
      return importProspectSheet({ prospects: await rowsOf(files.prospects), dnc: await rowsOf(files.dnc) });
    },
    onSuccess: refresh,
  });
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Import the Prospect Finder sheet</h3></div>
      <p className="crm-cell-sub">In the sheet, open each tab and use File → Download → Comma-separated values. Statuses carry over (Contacted → in rotation, Not a Fit → closed); notes and owners come along.</p>
      <div className="crm-form">
        <div className="crm-grid2">
          <label className="tr-field"><span className="tr-field__label">Prospects tab (CSV)</span>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFiles((f) => ({ ...f, prospects: e.target.files?.[0] }))} /></label>
          <label className="tr-field"><span className="tr-field__label">Do Not Contact tab (CSV)</span>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFiles((f) => ({ ...f, dnc: e.target.files?.[0] }))} /></label>
        </div>
        <div><Button size="sm" iconLeft={<Icon name="upload" size={14} />} onClick={() => run.mutate()} disabled={(!files.prospects && !files.dnc) || run.isPending}>{run.isPending ? 'Importing…' : 'Import'}</Button></div>
        {run.data && (
          <p className="crm-callout">
            {run.data.added} prospects added{run.data.duplicates ? `, ${run.data.duplicates} already here` : ''}{run.data.invalid ? `, ${run.data.invalid} rows without a domain skipped` : ''}{run.data.dncAdded ? ` · ${run.data.dncAdded} do-not-contact entries` : ''}.
          </p>
        )}
        <FormError error={run.error} />
      </div>
    </Card>
  );
}
